import { useEffect, useState } from 'react';
import { HpoItem, HpoSearchResult } from '@/types/hpo.types';
import { usePatient } from '@hooks/usePatient';
import { useMedplum } from '@medplum/react';
import { Coding, Condition, Encounter } from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import { HL7, PREFIXES, SYSTEMS } from '@consts/systems.consts';

// -----------------------------------------------------------------------------
export const usePatientHpo = () => {
  const [hpoData, setHpoData] = useState<HpoItem[]>([]);
  const [draftData, setDraftData] = useState<HpoItem[]>([]); // Draft data for edit mode
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HpoSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const patient = usePatient();
  const medplum = useMedplum();

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!patient?.id) {
      setHpoData([]);
      setDraftData([]);
      return;
    }

    const fetchHpoConditions = async () => {
      try {
        const response = await medplum.search('Condition', {
          patient: patient.id,
          code: `${SYSTEMS.HPO}|`,
          _sort: '-_lastUpdated',
        });

        if (response.entry && response.entry.length > 0) {
          const hpoItems = response.entry
            .map((entry) => {
              const condition = entry.resource as Condition;
              const coding = condition.code?.coding?.find(
                (c) => c.system === SYSTEMS.HPO
              );

              if (coding && condition.id) {
                // Extract encounter ID from encounter reference (format: "Encounter/123")
                const encounterId = condition.encounter?.reference?.replace('Encounter/', '');
                return {
                  id: coding.code || '',
                  name: coding.display || condition.code?.text || '',
                  value: condition.note?.[0]?.text || 'No definition available',
                  conditionId: condition.id,
                  encounterId: encounterId,
                  xrefs: [], // Existing items don't have xrefs stored
                } as HpoItem;
              }
              return null;
            })
            .filter((item): item is HpoItem => item !== null);

          setHpoData(hpoItems);
          setDraftData(hpoItems); // Initialize draft with original data
        } else {
          setHpoData([]);
          setDraftData([]);
        }
      } catch (error) {
        console.error('Error fetching HPO conditions:', error);
        setHpoData([]);
        setDraftData([]);
      }
    };

    fetchHpoConditions();
  }, [patient, medplum]);

  // ---------------------------------------------------------------------------
  const searchHpo = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://ontology.jax.org/api/hp/search?q=${encodeURIComponent(query)}`
      );
      const data = await response.json();

      const { terms, totalCount } = data;
      if (totalCount === 0) {
        setSearchResults([]);
        return;
      }

      const formattedResults = terms.map((item: any) => ({
        id: item.id || item.curie,
        label: item.label || item.name,
        definition: item.definition,
        xrefs: item.xrefs || [],
      }));

      setSearchResults(formattedResults);
    } catch (error) {
      console.error('Error fetching HPO data:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Add to draft (not saved yet)
  const addToDraft = (newItem: HpoSearchResult) => {
    if (draftData.some((item) => item.id === newItem.id)) {
      return;
    }

    const newHpoItem: HpoItem = {
      id: newItem.id,
      name: newItem.label,
      value: newItem.definition || 'No definition available',
      conditionId: undefined, // Will be set when saved
      xrefs: newItem.xrefs,
    };

    setDraftData((prevData) => [newHpoItem, ...prevData]);
  };

  // ---------------------------------------------------------------------------
  // Remove from draft (not saved yet)
  const removeFromDraft = (id: string) => {
    setDraftData((prevData) => prevData.filter((item) => item.id !== id));
  };

  // ---------------------------------------------------------------------------
  // Save all changes to database
  const saveChanges = async () => {
    setIsSaving(true);
    try {
      // Find items that were removed (in original but not in draft)
      const removedItems = hpoData.filter(
        (originalItem) =>
          !draftData.some((draftItem) => draftItem.id === originalItem.id)
      );

      // Delete removed items
      for (const item of removedItems) {
        if (item.conditionId) {
          await medplum.deleteResource('Condition', item.conditionId);
        }
      }

      // Find items that were added (in draft but not in original)
      const addedItems = draftData.filter(
        (draftItem) =>
          !hpoData.some((originalItem) => originalItem.id === draftItem.id)
      );

      // Add new items
      for (const item of addedItems) {
        const snomedCodings: Coding[] = [];
        if (item.xrefs && item.xrefs.length > 0) {
          item.xrefs.forEach((xref) => {
            if (!xref.startsWith(PREFIXES.SNOMED)) {
              return;
            }
            snomedCodings.push({
              system: SYSTEMS.SNOMED,
              code: xref.replace(PREFIXES.SNOMED, ''),
            });
          });
        }

        const codings = [
          {
            system: SYSTEMS.HPO,
            code: item.id,
            display: item.name,
          },
        ] as Coding[];

        if (snomedCodings.length > 0) {
          codings.push(...snomedCodings);
        }

        const newCondition: Condition = {
          resourceType: 'Condition',
          subject: {
            reference: `Patient/${patient?.id}`,
            display: patient?.name?.[0]?.text || `Patient ${patient?.id}`,
          },
          clinicalStatus: {
            coding: [
              {
                system: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE,
                code: 'active',
                display: 'Active',
              },
            ],
          },
          code: {
            coding: codings,
            text: item.name,
          },
          note: [
            {
              text: item.value,
            },
          ],
        };

        await medplum.createResource(newCondition);
      }

      // Update original data with draft data
      setHpoData([...draftData]);
      console.log('HPO changes saved successfully');
    } catch (error) {
      console.error('Error saving HPO changes:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Cancel changes - reset draft to original
  const cancelChanges = () => {
    setDraftData([...hpoData]);
  };

  // ---------------------------------------------------------------------------
  // Save a single HPO item to database immediately
  const saveHpoItem = async (
    item: HpoItem | HpoSearchResult,
    encounter?: Encounter
  ): Promise<HpoItem | null> => {
    if (!patient?.id) {
      console.error('Cannot save HPO: patient not available');
      return null;
    }

    // Check if already exists for this encounter
    if (encounter?.id) {
      const existingItem = hpoData.find(
        (h) => h.id === item.id && h.encounterId === encounter.id
      );
      if (existingItem) {
        return existingItem;
      }
    }

    setIsSaving(true);
    try {
      const hpoItem: HpoItem = 'label' in item
        ? {
            id: item.id,
            name: item.label,
            value: item.definition || 'No definition available',
            xrefs: item.xrefs,
          }
        : item;

      const snomedCodings: Coding[] = [];
      if (hpoItem.xrefs && hpoItem.xrefs.length > 0) {
        hpoItem.xrefs.forEach((xref) => {
          if (!xref.startsWith(PREFIXES.SNOMED)) {
            return;
          }
          snomedCodings.push({
            system: SYSTEMS.SNOMED,
            code: xref.replace(PREFIXES.SNOMED, ''),
          });
        });
      }

      const codings = [
        {
          system: SYSTEMS.HPO,
          code: hpoItem.id,
          display: hpoItem.name,
        },
      ] as Coding[];

      if (snomedCodings.length > 0) {
        codings.push(...snomedCodings);
      }

      const newCondition: Condition = {
        resourceType: 'Condition',
        subject: {
          reference: `Patient/${patient.id}`,
          display: patient.name?.[0]?.text || `Patient ${patient.id}`,
        },
        ...(encounter && {
          encounter: createReference(encounter),
        }),
        clinicalStatus: {
          coding: [
            {
              system: HL7.TERMINOLOGY.CS.CONDITION.CLINICAL.BASE,
              code: 'active',
              display: 'Active',
            },
          ],
        },
        code: {
          coding: codings,
          text: hpoItem.name,
        },
        note: [
          {
            text: hpoItem.value,
          },
        ],
      };

      const createdCondition = await medplum.createResource(newCondition);

      const savedItem: HpoItem = {
        ...hpoItem,
        conditionId: createdCondition.id,
        encounterId: encounter?.id,
      };

      // Update hpoData with the new item
      setHpoData((prev) => [...prev, savedItem]);
      setDraftData((prev) => [...prev, savedItem]);

      return savedItem;
    } catch (error) {
      console.error('Error saving HPO item:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete a single HPO item from database immediately
  const deleteHpoItem = async (item: HpoItem): Promise<void> => {
    if (!item.conditionId) {
      console.error('Cannot delete HPO: conditionId not available');
      return;
    }

    setIsSaving(true);
    try {
      await medplum.deleteResource('Condition', item.conditionId);

      // Update hpoData by removing the item by conditionId (not id, since same HPO can exist in multiple encounters)
      setHpoData((prev) => prev.filter((h) => h.conditionId !== item.conditionId));
      setDraftData((prev) => prev.filter((h) => h.conditionId !== item.conditionId));
    } catch (error) {
      console.error('Error deleting HPO item:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (draftData.length !== hpoData.length) {
      return true;
    }

    for (let i = 0; i < draftData.length; i++) {
      const draftItem = draftData[i];
      const originalItem = hpoData[i];

      if (
        !originalItem ||
        draftItem.id !== originalItem.id ||
        draftItem.name !== originalItem.name ||
        draftItem.value !== originalItem.value
      ) {
        return true;
      }
    }

    return false;
  };

  // ---------------------------------------------------------------------------
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchHpo(value);
  };

  // ---------------------------------------------------------------------------
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsLoading(false);
  };

  // ---------------------------------------------------------------------------
  const handleHpoSelection = (value: string) => {
    const selectedHpo = searchResults.find((result) => result.label === value);
    if (!selectedHpo) {
      console.error('Selected HPO term not found in search results');
      return;
    }

    addToDraft(selectedHpo);
    setTimeout(() => {
      clearSearch();
    }, 0);
  };

  // ---------------------------------------------------------------------------
  return {
    hpoData, // Original data (for view mode)
    draftData, // Draft data (for edit mode)
    searchQuery,
    searchResults,
    isLoading,
    isSaving,
    hasUnsavedChanges: hasUnsavedChanges(),
    handleSearchChange,
    handleHpoSelection,
    removeFromDraft, // Remove from draft only
    saveChanges, // Save all changes
    cancelChanges, // Cancel changes
    clearSearch,
    saveHpoItem, // Save a single HPO item immediately
    deleteHpoItem, // Delete a single HPO item immediately
  };
};
